/**
 * The pre-publish quality chip — the visible half of gradeArtifact Stage 0.
 *
 * WHY THIS EXISTS AT ALL. PostBody today renders readiness as a single 11px
 * sentence showing `blockingIssues[0]` plus "and N more", and ONLY when there
 * is an issue; its own comment argues "silence there already means ready."
 * That is precisely the founder's complaint — "users don't see the work going
 * into it so they don't see the value." A person who has never seen Mora
 * cannot distinguish "twelve constraints were checked and all passed" from
 * "nothing ran". This chip overturns that decision: it renders ALL SIX checks,
 * passes and skips included, each with a full sentence.
 *
 * Presentational + one callback. It fetches nothing, imports no Supabase, and
 * never grades — the verdict arrives as a prop so the same pure function backs
 * the chip, the edge writer and the tests.
 *
 * TONE. The monochromatic law reserves blue for send/focus/active-toggle, so a
 * PASS IS NEITHER GREEN NOR BLUE — it is plain muted foreground. Only warn and
 * fail carry colour. The fail value used to be a local `dark:text-project-red`
 * borrow, copied verbatim from PostBody's own local DESTRUCTIVE_ON_SURFACE so
 * the two could never render two different reds; both now read plain
 * `text-destructive`, which resolves to the shared `--destructive-text` token
 * in dark (index.css) instead of each file borrowing `--project-red` on its
 * own.
 */
import {
  CheckIcon,
  CircleDashedIcon,
  ShieldCheckIcon,
  WarningIcon,
  WarningOctagonIcon,
} from '@phosphor-icons/react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { useDialogContentContainer } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { type StatusTone, statusBadgeVariants } from '@/components/ui/status-badge';
import type { QualityFix, QualityStatus, QualityVerdict } from '@/lib/content-quality';
import { ITEM_META, ITEM_TITLE, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

const PASS_TONE = 'text-muted-foreground';
const WARN_TONE = 'text-warning';
const FAIL_TONE = 'text-destructive';

const STATUS_ICON: Record<
  QualityStatus,
  React.ComponentType<{ className?: string; weight?: 'regular' }>
> = {
  pass: CheckIcon,
  warn: WarningIcon,
  fail: WarningOctagonIcon,
  skipped: CircleDashedIcon,
};

const STATUS_TONE: Record<QualityStatus, string> = {
  pass: PASS_TONE,
  warn: WARN_TONE,
  fail: FAIL_TONE,
  skipped: 'text-muted-foreground/60',
};

/**
 * The PRESENTATIONAL contract, deliberately looser than `QualityVerdict`.
 *
 * This chip is the only visible "is it ready to ship" affordance in the
 * product, and for a long time it was the only one that existed at all: the
 * concept design workbench had a gate too (`evaluateReadyGate`) and rendered
 * it as a DISABLED MENU ROW's tooltip, buried in `⋯ → Actions`. Founder,
 * 2026-08-21: _"we have the peel on the AI Studio for the organic post that
 * says '1 to fix' … it does have a validator, but it looks a lot different on
 * the design page."_ Two gates, one job, wildly different legibility.
 *
 * Rather than fork the chip, the props widen. `QualityVerdict` (posts) and the
 * workbench's adapted ready gate both satisfy this shape structurally — the
 * post type's narrower `QualityCheckId` is assignable to `string`, so nothing
 * about the post path changes. Widening the PRESENTATION while leaving both
 * DOMAIN types intact is the point: the two gates genuinely check different
 * things and should not be merged, they should only look the same.
 */
export interface QualityChipCheck {
  id: string;
  label: string;
  status: QualityStatus;
  /** A full user-facing sentence, present on EVERY status including `pass`. */
  detail: string;
  fix?: QualityFix | null;
}

export interface QualityChipVerdict {
  checks: QualityChipCheck[];
  passCount: number;
  warnCount: number;
  failCount: number;
}

/**
 * Everything the chip says in words. Defaults are the post copy this chip
 * shipped with, so the existing call site is untouched by the widening.
 */
export interface QualityChipCopy {
  /** Popover heading, and the `aria-label` prefix on the trigger. */
  title: string;
  /** One sentence under the heading: what was checked, and by what. */
  description: string;
  /** Trigger label when nothing fails or warns. */
  passLabel: (passCount: number) => string;
  /** Trigger tone when everything passes. Defaults to neutral; the ready gate states success. */
  passTone?: StatusTone;
  /** Footer line under the rows. */
  footer: string;
}

const POST_COPY: QualityChipCopy = {
  title: 'Pre-publish checks',
  description:
    "Mora checks every post against this platform's real limits before it goes out — shape, format, caption length and links. No AI, no guessing.",
  passLabel: (count) => (count === 1 ? '1 check passed' : `${count} checks passed`),
  footer: 'Checked just now.',
};

export interface QualityChipProps {
  verdict: QualityChipVerdict;
  /** The verdict persisted at generation time, if any — drives the footer line. */
  storedVerdict?: QualityVerdict | null;
  /** Overrides the post-flavoured wording. Omit on the post surface. */
  copy?: QualityChipCopy;
  onApplyFix?: (fix: QualityFix) => void;
  isApplyingFix?: boolean;
  className?: string;
}

function CheckRow({
  check,
  onApplyFix,
  isApplyingFix,
}: {
  check: QualityChipCheck;
  onApplyFix?: (fix: QualityFix) => void;
  isApplyingFix?: boolean;
}) {
  const Icon = STATUS_ICON[check.status];
  const fix = check.fix ?? null;
  return (
    <div className="flex items-start gap-2 px-3 py-2">
      <Icon
        className={cn('mt-0.5 size-3.5 shrink-0', STATUS_TONE[check.status])}
        weight="regular"
      />
      <div className="min-w-0 flex-1">
        <div className={ITEM_TITLE}>{check.label}</div>
        <div className={cn(ITEM_META, 'mt-0.5')}>{check.detail}</div>
        {fix && onApplyFix ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isApplyingFix}
            onClick={() => onApplyFix(fix)}
            className="mt-1.5 h-7 gap-1 px-2 text-detail"
          >
            {isApplyingFix ? <Spinner size="sm" /> : null}
            <span>{fix.label}</span>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export const QualityChip: React.FC<QualityChipProps> = ({
  verdict,
  storedVerdict,
  copy = POST_COPY,
  onApplyFix,
  isApplyingFix,
  className,
}) => {
  const container = useDialogContentContainer();
  const { failCount, warnCount, passCount } = verdict;

  const TriggerIcon =
    failCount > 0 ? WarningOctagonIcon : warnCount > 0 ? WarningIcon : ShieldCheckIcon;
  const label =
    failCount > 0 && warnCount > 0
      ? `${failCount} to fix, ${warnCount} to review`
      : failCount > 0
        ? `${failCount} to fix`
        : warnCount > 0
          ? `${warnCount} to review`
          : copy.passLabel(passCount);

  // The trigger IS a StatusBadge, not a button dressed as one.
  //
  // It used to be `Button variant="outline" size="sm"` with an `h-7` override,
  // and measured 32px tall beside the 15px `StatusBadge` it sits next to —
  // 2.13x, plus a 1px border the badge does not have. Founder, 2026-08-21:
  // "the '2 to Fix' beside it is so big… three different text hierarchies and
  // three different styles."
  //
  // The type was never the problem: both are 11px/500. TWO container facts
  // were. The border, and a height override that silently lost — `Button`'s
  // `size="sm"` is `h-11 md:h-8`, and a bare `h-7` only cancels a utility at
  // the SAME breakpoint, so above 768px the primitive's touch-target height
  // won and the chip rendered at 32px, not the 28px its author intended. That
  // is rule 16's failure mode inverted: not a restated default, an override
  // that does not fully land.
  //
  // So rather than fight it with `h-7 md:h-7`, the trigger now borrows
  // `statusBadgeVariants` — the badge's own cva. The two become one family
  // that differs only in TONE, which is what they always were semantically:
  // one states the lifecycle stage, the other states what blocks it.
  const triggerTone: StatusTone =
    failCount > 0 ? 'destructive' : warnCount > 0 ? 'warning' : (copy.passTone ?? 'neutral');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${copy.title}: ${label}`}
          className={cn(
            statusBadgeVariants({ tone: triggerTone }),
            'transition-opacity hover:opacity-80',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          <TriggerIcon className="size-3" weight="regular" />
          <span>{label}</span>
        </button>
      </PopoverTrigger>
      {/* `collisionPadding` is load-bearing, not polish. Six check rows plus a
          header and a footer run ~525px; the shared PopoverContent caps itself
          at `--radix-popover-content-available-height`, which with no padding
          equals the FULL viewport. Measured on /calendar at 1440x900 the panel
          landed at top:0 and at 375x812 it landed at bottom:812 — flush to the
          window edge and sliced through the middle of a row, which reads as a
          rendering bug rather than as "there is more, scroll". The 12px gutter
          keeps the cut off the edge so the clipped row is legibly partial. */}
      <PopoverContent
        align="start"
        container={container}
        collisionPadding={12}
        className="w-80 max-w-[calc(100vw-2rem)] p-0"
      >
        <div className="px-3 pt-3">
          <div className={SECTION_LABEL}>{copy.title}</div>
          <p className={cn(ITEM_META, 'mt-1')}>{copy.description}</p>
        </div>
        <div className="mt-2 divide-y divide-border/40 border-t border-border/40">
          {verdict.checks.map((check) => (
            <CheckRow
              key={check.id}
              check={check}
              onApplyFix={onApplyFix}
              isApplyingFix={isApplyingFix}
            />
          ))}
        </div>
        <div className="border-t border-border/40 px-3 py-2 text-micro text-muted-foreground">
          {storedVerdict ? 'Checked when this was generated, and again just now.' : copy.footer}
        </div>
      </PopoverContent>
    </Popover>
  );
};
